import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Lock, CalendarPlus, ClipboardList, BookOpen, Phone, Mail, MapPin } from "lucide-react";

const TOOLS = [
  {
    id: "area-riservata",
    title: "Area riservata",
    desc: "Feedback sulla farmacoterapia e richiesta ricetta con codice personale",
    action: "Accedi all'area riservata",
    to: "/feedback",
    Icon: Lock,
    primary: true,
  },
  {
    id: "appuntamento",
    title: "Primo colloquio",
    desc: "Richiedi un primo appuntamento conoscitivo e di valutazione",
    action: "Richiedi un colloquio",
    to: "/appuntamento",
    Icon: CalendarPlus,
  },
  {
    id: "screening",
    title: "Test di screening",
    desc: "Questionari di autovalutazione",
    action: "Vai ai test",
    to: "/screening",
    Icon: ClipboardList,
  },
  {
    id: "psicoeducazione",
    title: "Psicoeducazione ADHD",
    desc: "Schede pratiche e strategie per la quotidianità",
    action: "Consulta le risorse",
    to: "/psicoeducazione",
    Icon: BookOpen,
  },
];

const AREE = [
  "Disturbi d'ansia e attacchi di panico",
  "Burnout e stress",
  "ADHD nell'adulto",
  "Autismo nell'adulto",
];

export default function Home() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 60);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <div className="flex-1" data-testid="home-bg">
      {/* PRESENTAZIONE BREVE */}
      <section className="max-w-4xl mx-auto px-5 pt-9 pb-8 sm:pt-12 sm:pb-10" data-testid="hero-section">
        <h1 className="font-display text-[32px] sm:text-4xl font-semibold tracking-tight text-[#142D43] leading-[1.18]">
          Dr. med. Nicolò Pipitone
        </h1>
        <p className="mt-2.5 text-base sm:text-lg font-semibold text-[#142D43]">
          Psichiatra e Psicoterapeuta FMH
        </p>
        <p className="mt-1 text-base sm:text-lg text-[#374151] leading-relaxed">
          Psichiatria e psicoterapia dell'adulto a Lugano-Paradiso.
        </p>
      </section>

      {/* STRUMENTI DELLO STUDIO */}
      <section id="servizi" className="max-w-4xl mx-auto px-5 pb-10 sm:pb-12 scroll-mt-20" data-testid="servizi-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="home-grid">
          {TOOLS.map((t) => (
            <Link
              key={t.id}
              to={t.to}
              data-testid={`home-card-${t.id}`}
              className={`home-card group flex flex-col rounded-[14px] border p-5 transition-colors duration-150 ${
                t.primary
                  ? "border-[#142D43] bg-white"
                  : "border-[#E5E7EB] bg-white hover:border-[#142D43]/50"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <t.Icon size={20} strokeWidth={1.8} className="text-[#142D43] shrink-0" />
                <h3 className="font-display text-xl font-semibold text-[#142D43] leading-snug">{t.title}</h3>
              </div>
              <p className="mt-2 text-base text-[#374151] leading-relaxed">{t.desc}</p>
              <span
                className={`mt-4 mt-auto inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-[10px] text-[15px] font-semibold transition-colors duration-150 ${
                  t.primary
                    ? "bg-[#142D43] text-white group-hover:bg-[#1E3F5C]"
                    : "text-[#142D43] border border-[#E5E7EB] group-hover:border-[#142D43]"
                }`}
              >
                {t.action} <ArrowRight size={15} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* PROFESSIONISTA E APPROCCIO */}
      <section className="bg-[#F4F6F8] border-y border-[#E5E7EB]" data-testid="approccio-section">
        <div className="max-w-4xl mx-auto px-5 py-9 sm:py-11">
          <div className="flex items-start gap-4 sm:gap-6">
            <img
              src="/assets/dr-pipitone-portrait.jpg"
              alt="Ritratto del Dr. med. Nicolò Pipitone"
              data-testid="profilo-photo"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover shrink-0 border border-[#E5E7EB]"
              loading="lazy"
            />
            <div className="min-w-0">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#142D43] leading-snug">
                Il professionista e l'approccio
              </h2>
              <p className="mt-3 text-base text-[#374151] leading-relaxed max-w-2xl">
                Medico psichiatra e psicoterapeuta a orientamento analitico e dinamico: valutazione clinica
                accurata, attenzione alla persona oltre il sintomo e percorsi terapeutici personalizzati per l'adulto.
              </p>
            </div>
          </div>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-base text-[#374151]" data-testid="aree-list">
            {AREE.map((a) => (
              <li key={a} className="flex items-center gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#142D43] shrink-0" aria-hidden="true" />
                {a}
              </li>
            ))}
          </ul>
          <Link
            to="/profilo"
            data-testid="profilo-cta"
            className="mt-6 inline-flex items-center gap-2 min-h-[44px] text-base font-semibold text-[#142D43] border-b-2 border-[#142D43] pb-0.5 hover:text-[#1E3F5C] hover:border-[#1E3F5C] transition-colors duration-150"
          >
            Conosci il mio approccio <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CONTATTI E STUDIO */}
      <section className="max-w-4xl mx-auto px-5 py-9 sm:py-11" data-testid="contatti-section">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#142D43] leading-snug">
          Contatti e Studio
        </h2>
        <div className="mt-4 flex flex-col" data-testid="home-contacts">
          <a
            href="https://www.google.com/maps/search/?api=1&query=Via+Generale+Guisan+8B,+6900+Lugano-Paradiso"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-address"
            className="flex items-center gap-3 min-h-[48px] text-base text-[#374151] hover:text-[#142D43] transition-colors duration-150"
          >
            <MapPin size={18} className="text-[#142D43] shrink-0" />
            via Generale Guisan 8B, 6900 Lugano-Paradiso
          </a>
          <a
            href="tel:+41916471417"
            data-testid="home-phone"
            className="flex items-center gap-3 min-h-[48px] text-base text-[#374151] hover:text-[#142D43] transition-colors duration-150"
          >
            <Phone size={18} className="text-[#142D43] shrink-0" />
            +41 91 647 14 17
          </a>
          <a
            href="mailto:pipitone@hin.ch"
            data-testid="home-email"
            className="flex items-center gap-3 min-h-[48px] text-base text-[#374151] hover:text-[#142D43] transition-colors duration-150"
          >
            <Mail size={18} className="text-[#142D43] shrink-0" />
            pipitone@hin.ch
          </a>
        </div>
      </section>
    </div>
  );
}
